// Cloudinary delivery helpers: a URL builder and an <img> component. No SDK,
// just URL construction (https://cloudinary.com/documentation/transformation_reference).

// The cloud name is public (it appears in every image URL), so a hardcoded
// fallback is safe and keeps cards rendering even when the env var is missing
// from a build. Otherwise cldUrl() emits `res.cloudinary.com//...` and 404s.
const CLOUD_NAME =
  import.meta.env?.VITE_CLOUDINARY_CLOUD_NAME ||
  (typeof process !== "undefined" &&
    process.env &&
    process.env.REACT_APP_CLOUDINARY_CLOUD_NAME) ||
  "shan123c";

const BASE_FOR = (cloud) =>
  `https://res.cloudinary.com/${cloud || CLOUD_NAME}/image/upload`;

// Matches a Cloudinary delivery URL and splits it right after `/upload/`, so
// transforms can be spliced in front of whatever follows (existing transforms,
// version, public id). Stored asset URLs (`listing.mainImage.secureUrl`,
// `agencyLogo.url`, CMS photos) are all of this form.
const CLOUDINARY_UPLOAD_URL = /^(https?:\/\/res\.cloudinary\.com\/[^/]+\/image\/upload\/)(.+)$/i;

// Transforms are an ordered list of [key, value] pairs, e.g.
// [["w", "720"], ["c", "fill"]] -> "w_720,c_fill".
const buildTransform = (pairs) =>
  Array.isArray(pairs) && pairs.length
    ? pairs.map(([k, v]) => `${k}_${v}`).join(",")
    : "";

// Anything that's already a URL or absolute browser path is a finished src:
// Vite-imported assets (`/assets/foo-hash.png`), data/blob URLs, other hosts.
const isAbsoluteSrc = (v) =>
  typeof v === "string" &&
  (/^(https?:|data:|blob:)/i.test(v) || v.startsWith("/"));

// Build a delivery URL. `publicId` may be a bare public id, a full Cloudinary
// URL (transforms get applied to it), or any other URL (returned unchanged).
export const cldUrl = (publicId, transforms, cloud) => {
  if (!publicId) return "";
  const t = buildTransform(transforms);
  if (isAbsoluteSrc(publicId)) {
    const m = t && publicId.match(CLOUDINARY_UPLOAD_URL);
    return m ? `${m[1]}${t}/${m[2]}` : publicId;
  }
  return t
    ? `${BASE_FOR(cloud)}/${t}/${publicId}`
    : `${BASE_FOR(cloud)}/${publicId}`;
};

// q_auto + f_auto unless the caller set quality/format itself.
const withAutoDefaults = (transforms = []) => {
  const keys = new Set(transforms.map(([k]) => k));
  return [
    ...transforms,
    ...(keys.has("q") ? [] : [["q", "auto"]]),
    ...(keys.has("f") ? [] : [["f", "auto"]]),
  ];
};

// <CldImage publicId="properties/abc" transforms={[["w", "320"]]} alt="" />
// Renders a plain <img>; other props (width, height, loading, className, ...)
// are forwarded. Images lazy-load unless the caller overrides `loading`.
export default function CldImage({
  publicId,
  transforms,
  cloudName,
  alt = "",
  loading = "lazy",
  ...rest
}) {
  return (
    <img
      src={cldUrl(publicId, withAutoDefaults(transforms), cloudName)}
      alt={alt}
      loading={loading}
      decoding='async'
      {...rest}
    />
  );
}

export { CldImage };
